import React, { useState, useEffect } from 'react';
import './FormScreen.css';
import { createClient } from '@supabase/supabase-js';

// Supabaseクライアントの設定（環境変数を使用）
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const FormScreen = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    phone: '',
    participation: '',
    meetingLocation: '',
    meetingTime: ''
  });
  
  const [errors, setErrors] = useState({});
  const [meetingTimes, setMeetingTimes] = useState([]);

  const meetingLocations = [
    { value: 'shibuya', label: '渋沢MIX（ご案内します）' },
    { value: 'store', label: '店舗やご指定の場所' },
    { value: 'online', label: 'オンライン' }
  ];


  const participationOptions = [
    { value: 'yes', label: '参加したい' },
    { value: 'listen', label: '話だけ聞きたい' },
    { value: 'no', label: '今回は不参加' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.company) {
      newErrors.company = '会社名を入力してください';
    }
    
    if (!formData.name) {
      newErrors.name = '名前を入力してください';
    }
    
    if (!formData.phone) {
      newErrors.phone = '電話番号を入力してください';
    }
    
    if (!formData.participation) {
      newErrors.participation = '参加意向を選択してください';
    }
    
    // 参加意向が「今回は不参加」以外の場合のみ面談場所と面談日時をチェック
    if (formData.participation && formData.participation !== 'no') {
      if (!formData.meetingLocation) {
        newErrors.meetingLocation = '面談場所を選択してください';
      }
      
      if (!formData.meetingTime) {
        newErrors.meetingTime = '面談日時を選択してください';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await submitFormData();
        onSubmit(formData);
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('送信中にエラーが発生しました。もう一度お試しください。');
      }
    }
  };

  // URLパラメータからIDを取得してデータベースから顧客情報を取得
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const customerId = urlParams.get('id');
    
    if (customerId && supabaseUrl && supabaseAnonKey) {
      fetchCustomerInfo(customerId);
    }
    
    // 面談日時を取得
    if (supabaseUrl && supabaseAnonKey) {
      fetchInterviewDates();
    }
  }, []);

  const fetchCustomerInfo = async (id) => {
    try {
      const { data, error } = await supabase
        .from('voting_customer_info')
        .select('name, company_name')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching customer info:', error);
        return;
      }

      if (data) {
        setFormData(prev => ({
          ...prev,
          company: data.company_name || '',
          name: data.name || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching customer info:', error);
    }
  };

  const fetchInterviewDates = async () => {
    try {
      const { data, error } = await supabase
        .from('voting_interview_date')
        .select('id, date_and_time')
        .order('date_and_time', { ascending: true });

      if (error) {
        console.error('Error fetching interview dates:', error);
        return;
      }

      if (data) {
        // 現在時刻より未来の日時のみをフィルタリング
        const currentTime = new Date();
        const futureDates = data.filter(item => {
          if (!item.date_and_time) return false;
          const interviewDate = new Date(item.date_and_time);
          return interviewDate > currentTime;
        });

        // 日時を適切な形式にフォーマット（45分間）
        const formattedTimes = futureDates.map(item => {
          const startTime = new Date(item.date_and_time);
          const endTime = new Date(startTime.getTime() + 45 * 60 * 1000); // 45分後
          
          const formatDate = (date) => {
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return { month, day, hours, minutes };
          };

          const start = formatDate(startTime);
          const end = formatDate(endTime);
          
          return {
            value: item.id,
            label: `${start.month}/${start.day} ${start.hours}:${start.minutes}~${end.hours}:${end.minutes}`,
            dateTime: item.date_and_time,
            startTime: startTime,
            endTime: endTime
          };
        });

        setMeetingTimes(formattedTimes);
      }
    } catch (error) {
      console.error('Error fetching interview dates:', error);
    }
  };

  // 祝日かどうかを判定する関数（簡易版）
  const isHoliday = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    // 日本の祝日の一部（基本的なもののみ）
    const holidays = [
      // 固定祝日
      `${year}-01-01`, // 元日
      `${year}-02-11`, // 建国記念の日
      `${year}-04-29`, // 昭和の日
      `${year}-05-03`, // 憲法記念日
      `${year}-05-04`, // みどりの日
      `${year}-05-05`, // こどもの日
      `${year}-08-11`, // 山の日
      `${year}-11-03`, // 文化の日
      `${year}-11-23`, // 勤労感謝の日
      `${year}-12-23`, // 天皇誕生日（現在）
      // その他の主要な祝日（簡易的な計算）
    ];
    
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return holidays.includes(dateString);
  };

  // 渋沢MIX用の時間フィルタリング
  const getFilteredMeetingTimes = () => {
    if (formData.meetingLocation !== 'shibuya') {
      return meetingTimes; // 渋沢MIX以外はすべての時間を表示
    }

    return meetingTimes.filter(time => {
      const date = time.startTime;
      const dayOfWeek = date.getDay(); // 0: 日曜, 1: 月曜, ..., 6: 土曜
      const hour = date.getHours();
      const endHour = time.endTime.getHours();
      
      // 日曜日（0）または祝日は除外
      if (dayOfWeek === 0 || isHoliday(date)) {
        return false;
      }
      
      // 平日（月〜金）: 10時〜21時（終了時間が21時まで）
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        return hour >= 10 && endHour <= 21;
      }
      
      // 土曜日: 10時〜18時（終了時間が18時まで）
      if (dayOfWeek === 6) {
        return hour >= 10 && endHour <= 18;
      }
      
      return false;
    });
  };

  // Google Chatに通知を送信する関数
  const sendGoogleChatNotification = async (formData, status) => {
    const webhookUrl = process.env.REACT_APP_GOOGLE_CHAT_WEBHOOK_URL;
    
    if (!webhookUrl) {
      console.warn('Google Chat Webhook URL not configured');
      return;
    }

    // 面談日時の情報を取得
    let meetingTimeInfo = '設定なし';
    let meetingLocationInfo = '設定なし';
    
    if (formData.participation !== 'no') {
      if (formData.meetingTime) {
        const selectedTime = meetingTimes.find(time => time.value === formData.meetingTime);
        if (selectedTime) {
          meetingTimeInfo = selectedTime.label;
        }
      }
      
      if (formData.meetingLocation) {
        const selectedLocation = meetingLocations.find(location => location.value === formData.meetingLocation);
        if (selectedLocation) {
          meetingLocationInfo = selectedLocation.label;
        }
      }
    }

    const message = {
      text: `📋 *実証実験フォーム回答通知*\n\n` +
            `**会社名**: ${formData.company}\n` +
            `**名前**: ${formData.name}\n` +
            `**電話番号**: ${formData.phone}\n` +
            `**参加意向**: ${status}\n` +
            `**面談場所**: ${meetingLocationInfo}\n` +
            `**面談日時**: ${meetingTimeInfo}\n\n` +
            `回答日時: ${new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}`
    };

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(message),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('Google Chat notification sent successfully');
    } catch (error) {
      console.error('Failed to send Google Chat notification:', error);
    }
  };

  const submitFormData = async () => {
    // 参加意向に基づいてstatusを設定
    let status = '';
    switch (formData.participation) {
      case 'yes':
        status = '参加希望';
        break;
      case 'listen':
        status = '話を聞きたい';
        break;
      case 'no':
        status = '不参加';
        break;
      default:
        status = '';
    }

    // 面談日時IDから実際の日時を取得
    let selectedDateTime = null;
    if (formData.meetingTime && formData.participation !== 'no') {
      const selectedTime = meetingTimes.find(time => time.value === formData.meetingTime);
      if (selectedTime) {
        selectedDateTime = selectedTime.dateTime;
      }
    }

    // データベースに保存
    const { data, error } = await supabase
      .from('voting_answer')
      .insert({
        name: formData.name,
        company_name: formData.company,
        phone_number: formData.phone,
        status: status,
        date_and_time: selectedDateTime
      });

    if (error) {
      throw error;
    }

    // Google Chatに通知を送信
    await sendGoogleChatNotification(formData, status);

    return data;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 参加意向が変更された場合、面談場所と面談日時をリセット
    if (field === 'participation') {
      if (value === 'no') {
        setFormData(prev => ({
          ...prev,
          [field]: value,
          meetingLocation: '',
          meetingTime: ''
        }));
        // エラーもクリア
        setErrors(prev => ({
          ...prev,
          meetingLocation: '',
          meetingTime: ''
        }));
      }
    }
    
    // 面談場所が変更された場合、面談日時をリセット
    if (field === 'meetingLocation') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        meetingTime: ''
      }));
      // 面談日時のエラーもクリア
      setErrors(prev => ({
        ...prev,
        meetingTime: ''
      }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const isFormValid = () => {
    const hasBasicInfo = formData.company && formData.name && formData.phone && formData.participation;
    
    if (!hasBasicInfo) return false;
    
    // 基本情報のエラーチェック
    const basicErrors = Object.keys(errors).filter(key => 
      key !== 'meetingLocation' && key !== 'meetingTime' && errors[key]
    );
    if (basicErrors.length > 0) return false;
    
    // 参加意向が「今回は不参加」の場合は基本情報のみでOK
    if (formData.participation === 'no') return true;
    
    // それ以外の場合は面談情報も必要
    const hasMeetingInfo = formData.meetingLocation && formData.meetingTime;
    const meetingErrors = Object.keys(errors).filter(key => 
      (key === 'meetingLocation' || key === 'meetingTime') && errors[key]
    );
    
    return hasMeetingInfo && meetingErrors.length === 0;
  };

  // 面談場所と面談日時を無効化するかどうかを判定
  const isMeetingDisabled = !formData.participation || formData.participation === 'no';
  const isMeetingTimeDisabled = isMeetingDisabled || !formData.meetingLocation;

  return (
    <div className="form-screen">
      <div className="form-container">
        <div className="logo-section">
          <img 
            src="https://ngayxdzippnqkzufqxhr.supabase.co/storage/v1/object/public/home-page-asset/asset/OpenReviewWhiteThemeLoog.png" 
            alt="OpenReview Logo" 
            className="logo"
          />
        </div>
        
        <div className="header-section">
          <h1>実証実験参加確認</h1>
          <p className="description">
            以下のフォームにご記入いただき、実証実験へのご参加意向をお聞かせください。<br />
            いただいた情報は実証実験の準備にのみ使用いたします。
          </p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="company">会社名</label>
            <input
              type="text"
              id="company"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              className={errors.company ? 'error' : ''}
              placeholder="会社名を入力してください"
            />
            {errors.company && <span className="error-message">{errors.company}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="name">名前</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={errors.name ? 'error' : ''}
              placeholder="お名前を入力してください"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">電話番号</label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={errors.phone ? 'error' : ''}
              placeholder="電話番号を入力してください"
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label>参加意向</label>
            <div className="participation-cards">
              {participationOptions.map((option) => (
                <div
                  key={option.value}
                  className={`participation-card ${formData.participation === option.value ? 'selected' : ''}`}
                  onClick={() => handleInputChange('participation', option.value)}
                >
                  <input
                    type="radio"
                    id={option.value}
                    name="participation"
                    value={option.value}
                    checked={formData.participation === option.value}
                    onChange={() => handleInputChange('participation', option.value)}
                  />
                  <label htmlFor={option.value}>{option.label}</label>
                </div>
              ))}
            </div>
            {errors.participation && <span className="error-message">{errors.participation}</span>}
          </div>

          <div className={`form-group ${isMeetingDisabled ? 'disabled' : ''}`}>
            <label>面談場所</label>
            <div className="participation-cards">
              {meetingLocations.map((location) => (
                <div
                  key={location.value}
                  className={`participation-card ${formData.meetingLocation === location.value ? 'selected' : ''} ${isMeetingDisabled ? 'disabled' : ''}`}
                  onClick={() => !isMeetingDisabled && handleInputChange('meetingLocation', location.value)}
                >
                  <input
                    type="radio"
                    id={location.value}
                    name="meetingLocation"
                    value={location.value}
                    checked={formData.meetingLocation === location.value}
                    onChange={() => !isMeetingDisabled && handleInputChange('meetingLocation', location.value)}
                    disabled={isMeetingDisabled}
                  />
                  <label htmlFor={location.value}>{location.label}</label>
                </div>
              ))}
            </div>
            {errors.meetingLocation && <span className="error-message">{errors.meetingLocation}</span>}
          </div>

          <div className={`form-group ${isMeetingTimeDisabled ? 'disabled' : ''}`}>
            <label htmlFor="meetingTime">面談日時</label>
            <select
              id="meetingTime"
              value={formData.meetingTime}
              onChange={(e) => handleInputChange('meetingTime', e.target.value)}
              className={errors.meetingTime ? 'error' : ''}
              disabled={isMeetingTimeDisabled}
            >
              <option value="">
                {!formData.meetingLocation ? '面談場所を先に選択してください' : '面談日時を選択してください'}
              </option>
              {getFilteredMeetingTimes().map((time, index) => (
                <option key={index} value={time.value}>{time.label}</option>
              ))}
            </select>
            {errors.meetingTime && <span className="error-message">{errors.meetingTime}</span>}
          </div>

          <button
            type="submit"
            className={`submit-button ${isFormValid() ? 'enabled' : 'disabled'}`}
            disabled={!isFormValid()}
          >
            送信
          </button>
        </form>
      </div>
    </div>
  );
};

export default FormScreen;